import { comonDataApi } from '@/api/config-setup'
import InputFile from '@/components/form-control/InputFile'
import { CopyOneIcon, DatabaseAlerIcon } from '@/components/icons'
import { XLSX_ACEPT, XLSX_FORMAT } from '@/constant/common'
import { CONFIG_SETUP_ROUTES } from '@/constant/route'
import { useAuthContext } from '@/features/auth/context'
import SetUpHeaderModal from '@/features/design-system/components/common/SetUpHeaderModal'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import useDeleteStationData from '@/features/design-system/hooks/setup/useDeleteStationData'
import MapViewRightMenu from '@/features/operations/components/pages/MapView/MapViewRightMenu'
import { NotifyService } from '@/helpers'
import { DATA_PAGINATION, SetupStation } from '@/types'
import { fileContentToBlob } from '@/ultils'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, Typography } from '@mui/material'
import { saveAs } from 'file-saver'
import { memo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PinStationsOnMap, StationTable } from '../components'
import CircularStatic from './CircularStatic'
import classes from './styles.module.scss'

interface Props {}

enum STEP {
  CHOOSE_FILE = 0,
  UPLOADING,
  UPLOAD_ERROR,
  REVIEW,
  PIN_STATION,
}

export const SetUpThird = memo((props: Props) => {
  const [progress, setProgress] = useState(0)
  const { interfaceId } = useAuthContext()

  const navigate = useNavigate()

  const [step, setStep] = useState(STEP.CHOOSE_FILE)
  const [station, setStation] = useState<DATA_PAGINATION<SetupStation>>()
  const [isDeleted, setIsDeleted] = useState(false)

  const onClose = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(interfaceId+""))
  }

  const onDownload = async () => {
    await comonDataApi.getStationTemplate(Number(interfaceId)).then((res) => {
      saveAs(res.data, 'Station_Template.xlsx')
    })
  }

  const onTryAgain = () => {
    setStep(STEP.CHOOSE_FILE)
  }

  const { mutate: deleteStations, isLoading: loadingReset } =
    useDeleteStationData()

  const handleDelete = () => {
    deleteStations(
      {
        interfaceId: interfaceId,
      },
      {
        onSuccess: () => {
          setIsDeleted(true)
        },
      },
    )
  }

  // useEffect(() => {
  //   comonDataApi.deleteStationFile(interfaceId).then(({ data }) => {
  //     if (data.success) {
  //       toast.success(data.message || '')
  //       setIsDeleted(data.success)
  //     }
  //   })
  // }, [interfaceId])

  const onChangeFile = async (file: File[]) => {
    if (!file?.length) return
    const data = new FormData()
    data.append('file', file[0])
    setStep(STEP.UPLOADING)
    await comonDataApi
      // .uploadStationFile(interfaceId, data, isDeleted, { setProgress })
      .uploadStationFile(Number(interfaceId), data, isDeleted)
      .then(({ data }) => {
        if (data.success) {
          setProgress(100)
          setTimeout(() => {
            setStation(data.data)
            setStep(STEP.REVIEW)
          }, 2000)
        } else {
          setStep(STEP.UPLOAD_ERROR)
          NotifyService.error('Upload file error')
          if (data.result) {
            saveAs(
              fileContentToBlob(
                data.result.fileContents,
                data.result.contentType,
              ),
              data.result.fileDownloadName,
            )
          }
        }
      })
      .catch((e) => {
        NotifyService.error(e?.message || 'Upload file error')
        setStep(STEP.UPLOAD_ERROR)
      })
  }

  const render = () => {
    switch (step) {
      case STEP.CHOOSE_FILE:
        return (
          <SetUpLayout
            title="Station"
            rightLayout={
              <Stack className={classes.mapViewRight}>
                <MapViewRightMenu />
              </Stack>
            }
            header={
              <SetUpHeaderModal title="Station Data" onCancel={onClose} />
            }
          >
            <Box className={classes.root}>
              <Typography className={classes.pageTitle} mt="52px">
                Station Data
              </Typography>
              <Stack mt="80px" alignItems="flex-start">
                <Typography className={classes.stepTitle}>
                  Step 1. Reset station data
                </Typography>
                <Box className={`${classes.actions} ${classes.actionsDow}`}>
                  <LoadingButton
                    className={classes.btnDownload}
                    onClick={handleDelete}
                    startIcon={<CopyOneIcon />}
                    loading={loadingReset}
                  >
                    Reset data
                  </LoadingButton>
                </Box>
                <Typography className={classes.stepTitle}>
                  Step 2. Fill in your data in the XLSX template
                </Typography>
                <Box className={`${classes.actions} ${classes.actionsDow}`}>
                  <Button
                    color="secondary"
                    className={classes.btnDownload}
                    onClick={onDownload}
                    startIcon={<CopyOneIcon />}
                  >
                    Download Excel Template
                  </Button>
                </Box>
                <Typography className={classes.stepTitle}>
                  Step 3. Upload the XLSX
                </Typography>

                <Box
                  sx={{
                    ...(!isDeleted && {
                      opacity: 0.5,
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }),
                  }}
                >
                  <InputFile
                    accept={XLSX_ACEPT}
                    formats={XLSX_FORMAT}
                    onChange={onChangeFile}
                  />
                </Box>
              </Stack>
            </Box>
          </SetUpLayout>
        )
      case STEP.UPLOADING:
        return (
          <SetUpLayout
            title="Station"
            rightLayout={
              <Stack className={classes.mapViewRight}>
                <MapViewRightMenu />
              </Stack>
            }
            header={
              <SetUpHeaderModal title="Station Data" onCancel={onClose} />
            }
          >
            <Box className={classes.root}>
              <Typography className={classes.pageTitle} mt="52px">
                Station Data
              </Typography>
              <CircularStatic progress={progress} />
              <Typography className={classes.title}>Processing Data</Typography>
              <Typography className={classes.description}>
                Please do not close the window
              </Typography>
            </Box>
          </SetUpLayout>
        )
      case STEP.UPLOAD_ERROR:
        return (
          <SetUpLayout
            title="Station"
            rightLayout={
              <Stack className={classes.mapViewRight}>
                <MapViewRightMenu />
              </Stack>
            }
            header={
              <SetUpHeaderModal title="Station Data" onCancel={onClose} />
            }
          >
            <Box className={classes.root}>
              <Typography className={classes.pageTitle} mt="52px">
                Station Data
              </Typography>
              <DatabaseAlerIcon className={classes.errorIcon} />
              <Typography className={classes.title}>
                Unable to import data
              </Typography>
              <Typography className={classes.description}>
                The file is not in the right format, or has typo errors
              </Typography>
              <Box className={classes.actions}>
                <Button
                  variant="contained"
                  className={classes.btnTry}
                  onClick={onTryAgain}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          </SetUpLayout>
        )
      case STEP.REVIEW:
        return (
          <StationTable
            data={station}
            onClose={onClose}
            onNext={() => setStep(STEP.PIN_STATION)}
          />
        )
      case STEP.PIN_STATION:
        return (
          <PinStationsOnMap
            interfaceId={Number(interfaceId)}
            data={station}
            onClose={onClose}
          />
        )
    }
  }
  return render()
})

export default SetUpThird
